import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Radar,
  RefreshCw,
  TrendingUp,
  Activity as ActivityIcon,
  Coins,
  Users as UsersIcon,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

// Friendly labels + colors per OSINT tool (the "reason" suffix from credit_ledger)
const TOOL_INFO = {
  whois:       { label: "WHOIS",            tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  dns:         { label: "DNS lookup",       tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  iploc:       { label: "IP Geolocation",   tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  hash:        { label: "Hash lookup",      tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  quickhash:   { label: "Quick hash",       tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  wayback:     { label: "Wayback Machine",  tier: "basic",  color: "text-sky-400 bg-sky-500/10 border-sky-500/30" },
  cve:         { label: "CVE search",       tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  subs:        { label: "Subdomains",       tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  github:      { label: "GitHub recon",     tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  ahmia:       { label: "Ahmia (Tor)",      tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  username_hunt: { label: "Username hunt", tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  exif:        { label: "EXIF metadata",    tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  reverse_image: { label: "Reverse image", tier: "medium", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  nmap:        { label: "Nmap scan",        tier: "heavy",  color: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
  shodan:      { label: "Shodan",           tier: "heavy",  color: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
  footprint:   { label: "Footprint",        tier: "heavy",  color: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
  pwned:       { label: "Have I Been Pwned", tier: "heavy", color: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
};

function ToolBadge({ tool }) {
  const info = TOOL_INFO[tool] || {
    label: tool,
    tier: "other",
    color: "text-zinc-300 bg-zinc-500/10 border-zinc-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border whitespace-nowrap ${info.color}`}
      data-testid={`osint-tool-badge-${tool}`}
    >
      {info.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
        <Icon size={13} strokeWidth={2} />
        <span className="uppercase tracking-wider">{label}</span>
      </div>
      <div className="font-mono font-bold text-2xl text-white">{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function formatRelTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return "—";
  }
}

export default function OSINTUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ total_runs: 0, total_credits_spent: 0, by_tool: [] });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agg, rec] = await Promise.all([
        api.get(`/osint/usage?days=${days}`),
        api.get("/osint/usage/recent?limit=40"),
      ]);
      setData(agg.data || {});
      setRecent(rec.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar OSINT usage");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const maxRuns = Math.max(1, ...(data.by_tool || []).map((t) => t.runs || 0));
  const uniqueUsers = new Set(recent.map((r) => r.telegram_id)).size;

  return (
    <div className="space-y-6" data-testid="osint-usage-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-black tracking-tight flex items-center gap-2">
            <Radar size={20} className="text-[#2AABEE]" />
            OSINT Usage
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Consumo agregado de las 17 herramientas OSINT (desde el ledger de créditos).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="osint-usage-range"
            className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-1.5 text-xs font-mono text-zinc-200 focus:outline-none focus:border-[#2AABEE]"
          >
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
            <option value={365}>1 año</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="osint-usage-refresh"
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={ActivityIcon}
          label="Ejecuciones"
          value={data.total_runs ?? 0}
          hint={`Últimos ${data.range_days || days} días`}
          testid="stat-total-runs"
        />
        <StatCard
          icon={Coins}
          label="Créditos gastados"
          value={data.total_credits_spent ?? 0}
          hint="Suma de cobros OSINT"
          testid="stat-total-credits"
        />
        <StatCard
          icon={TrendingUp}
          label="Herramientas activas"
          value={(data.by_tool || []).length}
          hint="Distintas en el periodo"
          testid="stat-active-tools"
        />
        <StatCard
          icon={UsersIcon}
          label="Usuarios únicos (recientes)"
          value={uniqueUsers}
          hint="Sobre últimos 40 eventos"
          testid="stat-unique-users"
        />
      </div>

      {/* Ranking */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="font-display font-bold text-sm">Ranking por herramienta</h2>
          <span className="text-[10px] text-zinc-500 font-mono">
            ordenado por ejecuciones desc
          </span>
        </div>
        {(data.by_tool || []).length === 0 ? (
          <div
            className="p-8 text-center text-sm text-zinc-500"
            data-testid="osint-usage-empty"
          >
            {loading ? "Cargando…" : "Sin ejecuciones registradas en el rango seleccionado."}
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {(data.by_tool || []).map((t) => {
              const pct = Math.round((t.runs / maxRuns) * 100);
              return (
                <div
                  key={t.tool}
                  className="px-4 py-3 grid grid-cols-12 items-center gap-3 hover:bg-zinc-900/40"
                  data-testid={`osint-usage-row-${t.tool}`}
                >
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-2">
                    <ToolBadge tool={t.tool} />
                  </div>
                  <div className="col-span-8 sm:col-span-5">
                    <div className="h-2 w-full rounded-sm bg-zinc-800 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#2AABEE] to-cyan-400"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <div
                    className="col-span-4 sm:col-span-1 text-right font-mono text-sm text-white"
                    data-testid={`osint-runs-${t.tool}`}
                  >
                    {t.runs}
                  </div>
                  <div className="col-span-6 sm:col-span-2 text-right font-mono text-xs text-amber-400">
                    {t.credits_spent} créditos
                  </div>
                  <div className="col-span-6 sm:col-span-1 text-right font-mono text-[10px] text-zinc-500">
                    {t.unique_users_count} usr · {formatRelTime(t.last_used_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent feed */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="font-display font-bold text-sm flex items-center gap-2">
            <Clock size={14} className="text-zinc-400" />
            Actividad reciente
          </h2>
          <span className="text-[10px] text-zinc-500 font-mono">
            {recent.length} eventos
          </span>
        </div>
        {recent.length === 0 ? (
          <div
            className="p-8 text-center text-sm text-zinc-500"
            data-testid="osint-usage-recent-empty"
          >
            {loading ? "Cargando…" : "Aún no hay ejecuciones OSINT."}
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {recent.map((r, idx) => (
              <div
                key={`${r.telegram_id}-${r.created_at}-${idx}`}
                className="px-4 py-2.5 grid grid-cols-12 items-center gap-3 text-xs hover:bg-zinc-900/40"
                data-testid={`osint-usage-recent-${idx}`}
              >
                <div className="col-span-4 sm:col-span-3">
                  <ToolBadge tool={r.tool} />
                </div>
                <div className="col-span-5 sm:col-span-5 truncate">
                  <span className="text-zinc-300">{r.display_name}</span>
                  <span className="text-zinc-600 ml-2 font-mono">#{r.telegram_id}</span>
                </div>
                <div className="col-span-3 sm:col-span-2 text-right font-mono text-amber-400">
                  {Math.abs(r.delta || 0)} cr
                </div>
                <div className="hidden sm:block sm:col-span-2 text-right font-mono text-[10px] text-zinc-500">
                  {formatRelTime(r.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
